import { AnimatePresence, motion } from "motion/react";
import { Field, NumberInput, TextInput } from "../../components/fields";
import { Button, IconButton } from "../../components/ui";
import { fieldLabels } from "../../i18n/fieldLabels";
import { pick } from "../../i18n/labels";
import { useT } from "../../i18n/strings";
import { micro } from "../../motion";
import s from "./form.module.css";

export interface PartDraft {
  key: string;
  label: string;
  width_cm: number | null;
  height_cm: number | null;
  depth_cm: number | null;
}

interface PartsEditorProps {
  parts: PartDraft[];
  errors: Partial<Record<string, string>>;
  onChange: (index: number, changes: Partial<PartDraft>) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

export function partError(
  errors: Partial<Record<string, string>>,
  index: number,
  field: keyof PartDraft,
): string | undefined {
  return errors[`parts.${index}.${field}`] ?? errors[`parts.${index}`];
}

/** One card per panel or component (diptych, triptych, set). */
export function PartsEditor({ parts, errors, onChange, onAdd, onRemove }: PartsEditorProps) {
  const t = useT();

  return (
    <div className={s.parts}>
      {parts.length === 0 && <p className={s.note}>{t.form.partsEmpty}</p>}

      <AnimatePresence initial={false}>
        {parts.map((part, index) => (
          <motion.div
            key={part.key}
            className={s.partLine}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={micro}
          >
            <div className={s.partHead}>
              <span>{t.form.partLine(index + 1)}</span>
              <IconButton name="delete" label={t.actions.remove} onClick={() => onRemove(index)} />
            </div>

            <Field
              label={fieldLabels.part_label}
              optional
              error={partError(errors, index, "label")}
            >
              <TextInput
                auto
                value={part.label}
                placeholder={t.form.partLabelPlaceholder}
                onChange={(value) => onChange(index, { label: value })}
              />
            </Field>

            <Field
              label={fieldLabels.artworkDimensions}
              optional
              error={
                partError(errors, index, "width_cm") ||
                partError(errors, index, "height_cm") ||
                partError(errors, index, "depth_cm")
              }
            >
              <div className={s.yearRow}>
                <NumberInput
                  min={0}
                  value={part.width_cm}
                  invalid={Boolean(partError(errors, index, "width_cm"))}
                  onChange={(value) => onChange(index, { width_cm: value })}
                />
                <NumberInput
                  min={0}
                  value={part.height_cm}
                  invalid={Boolean(partError(errors, index, "height_cm"))}
                  onChange={(value) => onChange(index, { height_cm: value })}
                />
                <NumberInput
                  min={0}
                  value={part.depth_cm}
                  invalid={Boolean(partError(errors, index, "depth_cm"))}
                  onChange={(value) => onChange(index, { depth_cm: value })}
                />
              </div>
              <p className={s.partDimHint}>
                {pick(fieldLabels.width)} · {pick(fieldLabels.height)} · {pick(fieldLabels.depth)} (
                {t.form.unit})
              </p>
            </Field>
          </motion.div>
        ))}
      </AnimatePresence>

      <Button variant="secondary" icon="add" onClick={onAdd}>
        {t.form.addPart}
      </Button>
    </div>
  );
}
